import { inject, Injectable } from "@angular/core";
import { TranslationService } from "@app/kernel/translations/translation.service";
import { InterpolationParameters } from "@ngx-translate/core";
import { MessageService } from "primeng/api";
import { SessionStorageService } from "../../services/session-storage.service";

@Injectable()
export class ToastService {
  private messageService = inject(MessageService);
  private translationService = inject(TranslationService);
  private sessionStorage = inject(SessionStorageService);

  /**
   * Shows a success message
   * @param message
   * @param summary
   */
  success(
    message: string,
    summary = "Success",
    options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters }
  ) {
    this.messageService.add({
      severity: "success",
      summary: this.translationService.instant(summary, options?.summaryTranslationValue),
      detail: this.translationService.instant(message, options?.detailTranslationValue),
    });
  }

  /**
   * Shows an info message
   * @param message
   * @param summary
   */
  info(
    message: string,
    summary = "Info",
    options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters }
  ) {
    this.messageService.add({
      severity: "info",
      summary: this.translationService.instant(summary, options?.summaryTranslationValue),
      detail: this.translationService.instant(message, options?.detailTranslationValue),
    });
  }

  /**
   * Shows a warning message
   * @param message
   * @param summary
   */
  warn(
    message: string,
    summary = "Warn",
    options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters }
  ) {
    this.messageService.add({
      severity: "warn",
      summary: this.translationService.instant(summary, options?.summaryTranslationValue),
      detail: this.translationService.instant(message, options?.detailTranslationValue),
    });
  }

  /**
   * Shows an error message
   * @param message
   * @param summary
   */
  error(
    message: string,
    summary = "Error",
    options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters }
  ) {
    this.messageService.add({
      severity: "error",
      summary: this.translationService.instant(summary, options?.summaryTranslationValue),
      detail: this.translationService.instant(message, options?.detailTranslationValue),
    });
  }

  snackbar(message: string, btns?: { class: string; text: string; onClick: (closeFn: () => void) => void }[], sticky = true) {
    this.messageService.add({
      key: "snackbar",
      severity: "custom",
      styleClass: "toast-snackbar",
      detail: message,
      data: { btns },
      sticky,
    });
  }

  /**
   * Clears all messages
   */
  clear(key?: string) {
    this.messageService.clear(key);
  }

  /**
   * Store message in session storage
   * @param message
   * @param summary
   */
  storeMessageInSessionStorage(
    message: string,
    summary: "Success" | "Info" | "Warn" | "Error",
    options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters }
  ) {
    this.sessionStorage.set("message", JSON.stringify({ message, summary, options }));
  }

  /**
   * Get stored message from session storage
   */
  getMessageFromSessionStorage() {
    return this.sessionStorage.get("message") as string | null;
  }

  /**
   * Remove stored message from session storage
   */
  removeMessageFromSessionStorage() {
    this.sessionStorage.remove("message");
  }

  /**
   * Pop stored message from session storage
   */
  popMessageFromSessionStorage() {
    const messageData = this.getMessageFromSessionStorage();
    if (messageData) {
      const message = JSON.parse(messageData) as {
        message: string;
        summary: "Success" | "Info" | "Warn" | "Error";
        options?: { summaryTranslationValue?: InterpolationParameters; detailTranslationValue?: InterpolationParameters };
      };
      switch (message.summary) {
        case "Success":
          this.success(message.message, message.summary, message?.options);
          break;
        case "Info":
          this.info(message.message, message.summary, message?.options);
          break;
        case "Warn":
          this.warn(message.message, message.summary, message?.options);
          break;
        case "Error":
          this.error(message.message, message.summary, message?.options);
          break;
      }
      this.removeMessageFromSessionStorage();
    }
  }
}
